import React from 'react'
import { Field, useFormikContext } from 'formik'
import * as Yup from 'yup'
import { FormDatePicker, GraphQLForm } from 'src/form'
import { UpdateQueryOptions } from '@apollo/client'
import useAddPatientHistoryEntry from 'src/participantHistory/useAddPatientHistoryEntry'
import FinancialEligibilityHealthPlanFields, {
  FINANCIAL_ELIGIBILITY_HEALTH_PLAN_FORM_SCHEMA,
} from 'src/participantHistory/financialEligibility/FinancialEligibilityHealthPlanFields'
import IntakeNoteFields from 'src/participantHistory/intakeNote/IntakeNoteFields'
import { PatientHistoryFormType } from 'src/participantHistory/constants'
import { createIntakeNoteSchema } from 'src/participantHistory/intakeNote/intakeNoteSchemas'
import { checkIfSelfPaySource } from 'src/participant/participantUtils'
import { usePatientSource } from 'src/participant/usePatientSource'
import { DateTime } from 'luxon'
import { useLoadedNoteDraft } from 'src/noteDraft/useNoteDraft'
import { NoteDraftLoadError } from 'src/noteDraft/NoteDraftAlerts'
import { clearFormFromLocalStorage } from 'src/participantHistoryForm/form/persistFormHelpers'
import FormPersistor from 'src/participantHistoryForm/form/FormPersistor'
import ParticipantHistoryFormActions from './ParticipantHistoryFormActions'
import { PatientHistorySchema, PatientHistoryEntry } from './types'

const createPatientHistorySchema = (
  uid: string,
  formType: PatientHistoryFormType,
  { requireBillingCodes }: { requireBillingCodes: boolean }
): PatientHistorySchema => {
  switch (formType) {
    case 'INTAKE_NOTE': {
      return Yup.object({
        uid: Yup.string().required().default(uid),
        date: Yup.string().default(DateTime.now().toFormat('yyyy-MM-dd')).required(),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        type: Yup.string().default(formType).required() as Yup.AnySchema,
        value: createIntakeNoteSchema({ requireBillingCodes }).default({}).required(),
      }).required()
    }
    case 'FINANCIAL_ELIGIBILITY_HEALTH_PLAN':
      return Yup.object({
        uid: Yup.string().required().default(uid),
        date: Yup.string().default(DateTime.now().toFormat('yyyy-MM-dd')).required(),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        type: Yup.string().default(formType).required() as Yup.AnySchema,
        value: FINANCIAL_ELIGIBILITY_HEALTH_PLAN_FORM_SCHEMA.default({}).required(),
      }).required()
    default:
      throw new Error('unknown form type')
  }
}

type Props = {
  uid: string
  formType: PatientHistoryFormType
  afterSubmittedCallback: () => void
}

const ParticipantHistoryForm = ({ uid, formType, afterSubmittedCallback }: Props) => {
  // Only the intake note branch cares about the source, and the other branches must not pay for
  // the query. While the source is still loading the submit button stays disabled, so requiring
  // codes by default cannot block anyone — it only avoids briefly accepting a note without one.
  const { source } = usePatientSource(uid, { skip: formType !== 'INTAKE_NOTE' })
  const validationSchema = createPatientHistorySchema(uid, formType, {
    requireBillingCodes: !checkIfSelfPaySource(source),
  })

  const addPatientHistoryEntry = useAddPatientHistoryEntry()

  const onSubmit = async (options: UpdateQueryOptions<PatientHistoryEntry>) => {
    await addPatientHistoryEntry(options.variables!)
    clearFormFromLocalStorage(uid, formType)
    afterSubmittedCallback()
  }

  const loaded = useLoadedNoteDraft({ uid, type: formType })

  if (loaded.failed) {
    return <NoteDraftLoadError />
  }

  if (!loaded.ready) {
    return null
  }

  return (
    <GraphQLForm
      onSubmit={onSubmit}
      validationSchema={validationSchema}
      initialValues={loaded.draft ?? undefined}
      render={() => <Form uid={uid} formType={formType} />}
    />
  )
}

const Form = ({ uid, formType }: { uid: string; formType: PatientHistoryFormType }) => {
  const { isSubmitting, submitForm } = useFormikContext<PatientHistoryEntry>()

  return (
    <>
      <FormPersistor uid={uid} formType={formType} />
      <Field component={FormDatePicker} label="Date of event" name="date" />
      {formType === 'FINANCIAL_ELIGIBILITY_HEALTH_PLAN' && <FinancialEligibilityHealthPlanFields uid={uid} />}
      {formType === 'INTAKE_NOTE' && <IntakeNoteFields uid={uid} />}
      <ParticipantHistoryFormActions
        uid={uid}
        historyType={formType}
        isSubmitting={isSubmitting}
        onSubmit={submitForm}
      />
    </>
  )
}

export default ParticipantHistoryForm
